import React, { useEffect, useRef } from 'react';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import { motion, PanInfo, useAnimation } from 'framer-motion';
import NavbarWalletBalance from './NavbarWalletBalance';
import chainIcon from '../../constants/icons/chain_icon.png';
import accountIcon from '../../constants/icons/account_icon.png';
import balanceIcon from '../../constants/icons/coin_icon.png';

// types
interface IProps {
  isSheetOpen: boolean;
  onSheetClose: () => void;
  onSheetOpen: () => void;
}

// wallet
const shorter = (str: any) =>
  str?.length > 8 ? `${str.slice(0, 6)}...${str.slice(-4)}` : str;

// animations
const usePrevious = (value: boolean) => {
  const previousValueRef = useRef<boolean>();

  useEffect(() => {
    previousValueRef.current = value;
  }, [value]);

  return previousValueRef.current;
};

// component
const NavbarnavbarBottomSheet: React.FC<IProps> = ({
  isSheetOpen,
  onSheetClose,
  onSheetOpen
}) => {
  // animations
  const prevIsSheetOpen = usePrevious(isSheetOpen);
  const controls = useAnimation();

  const vh = window.innerHeight;

  function onDragEnd(
    event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) {
    const shouldClose = info.velocity.y > 20 || info.point.y > vh - 88;
    if (shouldClose) {
      controls.start('hidden');
      onSheetClose();
    } else {
      controls.start({ y: 0 });
      onSheetOpen();
    }
  }

  useEffect(() => {
    if (prevIsSheetOpen && !isSheetOpen) {
      controls.start('hidden');
    } else if (!prevIsSheetOpen && isSheetOpen) {
      controls.start('visible');
    }
  }, [controls, isSheetOpen, prevIsSheetOpen]);

  // wallet
  const { chainId, account, active, deactivate } = useWeb3React<Web3Provider>();

  async function disconnect() {
    try {
      await deactivate();
      controls.start('hidden');
      onSheetClose();
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <motion.div
      key="bottom"
      drag="y"
      onDragEnd={onDragEnd}
      initial="hidden"
      animate={controls}
      transition={{
        type: 'spring',
        damping: 40,
        stiffness: 400
      }}
      variants={{
        visible: { top: vh - 178, height: 178 },
        hidden: { top: vh, height: 0 }
      }}
      dragConstraints={{ top: 0 }}
      dragElastic={0}
      style={{
        position: 'fixed',
        backgroundColor: '#202020',
        width: '100%',
        maxWidth: 420,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10
      }}
    >
      <div className="navbarBottomSheetLine" />
      <div className="navbarBottomSheetCard navbarBottomSheetWrapper">
        <ul className="navbarBottomSheetDivider">
          <li className="navbarBottomSheetListItem">
            <img
              src={chainIcon}
              alt="chainIcon"
              className="navbarBottomSheetIcon"
            />
            ChainId:
          </li>
          <li className="navbarBottomSheetListItem">
            <img
              src={accountIcon}
              alt="accountIcon"
              className="navbarBottomSheetIcon"
            />
            Account:
          </li>
          <li className="navbarBottomSheetListItem">
            <img
              src={balanceIcon}
              alt="balanceIcon"
              className="navbarBottomSheetIcon"
            />
            Balance:
          </li>
        </ul>
        <ul className="navbarBottomSheetDivider">
          <li>{chainId}</li>
          <li>{shorter(account)}</li>
          <li>{active && <NavbarWalletBalance />}</li>
        </ul>
      </div>
      <div className="navbarBottomSheetCard navbarBottomSheetCenter">
        <button
          type="button"
          className="navbarBottomSheetLogoutButton"
          onClick={disconnect}
        >
          Logout
        </button>
      </div>
    </motion.div>
  );
};

export default NavbarnavbarBottomSheet;
